// Field-level encryption envelope (SECURITY.md §3).
// {"v":1,"k":<key_version>,"iv":<b64url 12 bytes>,"ct":<b64url ciphertext+tag>}
// AES-256-GCM; additional authenticated data = row id, so an envelope cannot be
// moved between rows.
import { b64url, fromB64url, randomBytes, utf8, buf } from './encoding';

export interface Envelope { v: 1; k: number; iv: string; ct: string }

export interface ClinicKey { version: number; key: CryptoKey }

export async function importAesKey(raw: Uint8Array, extractable = false): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', buf(raw), { name: 'AES-GCM' }, extractable, ['encrypt', 'decrypt']);
}

export async function generateAesKey(): Promise<Uint8Array> {
  return randomBytes(32);
}

export async function encryptFields(ck: ClinicKey, rowId: string, fields: Record<string, unknown>): Promise<string> {
  const iv = randomBytes(12);
  const pt = utf8.encode(JSON.stringify(fields));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: buf(iv), additionalData: buf(utf8.encode(rowId)) }, ck.key, buf(pt));
  const env: Envelope = { v: 1, k: ck.version, iv: b64url(iv), ct: b64url(new Uint8Array(ct)) };
  return JSON.stringify(env);
}

export function parseEnvelope(s: string): Envelope {
  const e = JSON.parse(s) as Envelope;
  if (e.v !== 1 || typeof e.k !== 'number' || !e.iv || !e.ct) throw new Error('bad envelope');
  return e;
}

export async function decryptFields<T = Record<string, unknown>>(keys: Map<number, CryptoKey>, rowId: string, enc: string): Promise<T> {
  const e = parseEnvelope(enc);
  const key = keys.get(e.k);
  if (!key) throw new Error(`no key for version ${e.k}`);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: buf(fromB64url(e.iv)), additionalData: buf(utf8.encode(rowId)) },
    key,
    buf(fromB64url(e.ct)),
  );
  return JSON.parse(utf8.decode(new Uint8Array(pt))) as T;
}
